import { ref } from 'vue'
import { defineStore } from 'pinia'
import axios, { AxiosError } from 'axios'
import * as Sentry from '@sentry/vue'

const LS_KEY_PREFIX = 'finnecto:subdomain:'
const TTL_MS = 60 * 60 * 1000 // 1 hour

interface CacheEntry {
  valid: boolean
  expiresAt: number
  // Auth0 organization (society.cognito_user_group) for this subdomain. Optional so cache
  // entries written before this field shipped still parse.
  organization?: string | null
}

export const useSubdomainStore = defineStore('subdomain', () => {
  // L1 — memory cache: subdomain → { valid, expiresAt }
  const memCache = ref<Record<string, CacheEntry>>({})

  function lsKey(subdomain: string): string {
    return `${LS_KEY_PREFIX}${subdomain}`
  }

  /**
   * Reads a cache entry from localStorage.
   * On corrupted JSON (or wrong shape), wipes the key and returns null so
   * the caller falls through to a fresh network fetch.
   */
  function readFromStorage(subdomain: string): CacheEntry | null {
    try {
      const raw = localStorage.getItem(lsKey(subdomain))
      if (!raw) return null

      const entry = JSON.parse(raw) as unknown

      if (
        typeof entry !== 'object' ||
        entry === null ||
        typeof (entry as CacheEntry).valid !== 'boolean' ||
        typeof (entry as CacheEntry).expiresAt !== 'number'
      ) {
        throw new Error('Unexpected cache shape')
      }

      return entry as CacheEntry
    } catch {
      localStorage.removeItem(lsKey(subdomain))
      return null
    }
  }

  function writeToStorage(subdomain: string, valid: boolean, expiresAt: number, organization: string | null): void {
    const entry: CacheEntry = { valid, expiresAt, organization }
    localStorage.setItem(lsKey(subdomain), JSON.stringify(entry))
  }

  /** The Auth0 organization cached for this subdomain (memory → localStorage), or null. */
  function getOrganization(subdomain: string): string | null {
    const memEntry = memCache.value[subdomain]
    if (memEntry?.organization != null) return memEntry.organization
    return readFromStorage(subdomain)?.organization ?? null
  }

  /**
   * Returns whether a subdomain is valid, going through:
   *   L1 (Pinia) → L2 (localStorage) → network
   *
   * Throws if the network request fails so the router guard can decide
   * how to handle the error (e.g. fail-open vs. fail-closed).
   */
  async function checkSubdomain(subdomain: string): Promise<boolean> {
    const now = Date.now()

    // L1 — Pinia memory cache
    const memEntry = memCache.value[subdomain]
    if (memEntry && memEntry.expiresAt > now) {
      return memEntry.valid
    }

    // L2 — localStorage
    const lsEntry = readFromStorage(subdomain)
    if (lsEntry) {
      if (lsEntry.expiresAt > now) {
        // Hydrate L1 from L2 and return
        memCache.value[subdomain] = lsEntry
        return lsEntry.valid
      }
      // Expired — evict from storage
      localStorage.removeItem(lsKey(subdomain))
    }

    // Network fallback — unauthenticated, intentionally no Authorization header
    // Telemetry: see .planning/2026-05-21-invalid-tenant-investigation.md
    let response
    try {
      response = await axios.get<{ valid: boolean; organization?: string | null }>(
        `${import.meta.env.VITE_API_URL}/api/validate-subdomain`
      )
    } catch (err) {
      const axiosErr = err as AxiosError<unknown>
      Sentry.captureException(err, {
        tags: { failure_point: 'subdomain_validate_network' },
        extra: {
          subdomain,
          origin: window.location.origin,
          hostname: window.location.hostname,
          api_url: import.meta.env.VITE_API_URL,
          axios_code: axiosErr?.code,
          response_status: axiosErr?.response?.status,
          response_data: axiosErr?.response?.data,
        },
      })
      throw err
    }

    const valid = response.data.valid
    const organization = response.data.organization ?? null
    const expiresAt = now + TTL_MS

    // Write to both caches
    memCache.value[subdomain] = { valid, expiresAt, organization }
    writeToStorage(subdomain, valid, expiresAt, organization)

    return valid
  }

  return { memCache, checkSubdomain, getOrganization }
})
